import { useCallback, useEffect, useRef, useState } from 'react';

import { AxiosError } from 'axios';

import { apiStrapi } from 'services/apiService';
import { BannerCockpit, UseStrapiBannersCockpitReturn } from 'types/strapiBanners';

// Cache global para os banners com timestamp
interface CacheEntry {
  data: BannerCockpit;
  timestamp: number;
  expiresAt: number;
  retryCount: number;
}

// Estado global para controle de inicialização
let globalCache: CacheEntry | null = null;
let globalInitializationPromise: Promise<BannerCockpit | null> | null = null;
let globalIsInitializing = false;

// Configurações
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
const REQUEST_TIMEOUT = 15000; // 15 segundos
const MAX_RETRIES = 5;
const RETRY_DELAYS = [1000, 2000, 4000, 8000, 15000]; // Delays progressivos

// Função para verificar se os headers do Strapi estão configurados
const areStrapiHeadersConfigured = (): boolean => {
  try {
    const instance = apiStrapi.getInstance();
    const headers = instance.defaults.headers.common;

    const hasAuth = !!headers.Authorization;
    const hasOrgUUID = !!headers['X-Organization-UUID'];
    const hasUserPool = !!headers['X-Cognito-UserPool'];

    return hasAuth && hasOrgUUID && hasUserPool;
  } catch (error) {
    return false;
  }
};

// Função para aguardar headers serem configurados
const waitForHeaders = (): Promise<boolean> => {
  return new Promise(resolve => {
    if (areStrapiHeadersConfigured()) {
      resolve(true);
      return;
    }

    // Aguarda até 30 segundos pelos headers (mais tempo para inicialização da app)
    const maxWaitTime = 30000;
    const checkInterval = 200;
    let elapsedTime = 0;

    const checkHeaders = () => {
      if (areStrapiHeadersConfigured()) {
        resolve(true);
        return;
      }

      elapsedTime += checkInterval;
      if (elapsedTime >= maxWaitTime) {
        console.warn('[useStrapiBannersCockpit] Timeout aguardando headers após 30s');
        resolve(false);
        return;
      }

      setTimeout(checkHeaders, checkInterval);
    };

    checkHeaders();
  });
};

// Função para limpar cache expirado
const clearExpiredCache = (): void => {
  if (globalCache && Date.now() > globalCache.expiresAt) {
    console.log('[useStrapiBannersCockpit] Cache expirado, limpando...');
    globalCache = null;
  }
};

// Função principal para buscar banners com retry inteligente
const fetchBannersWithRetry = async (): Promise<BannerCockpit | null> => {
  // Limpa cache expirado
  clearExpiredCache();

  // Se já temos banners válidos em cache, usa eles
  if (globalCache && Date.now() <= globalCache.expiresAt) {
    console.log('[useStrapiBannersCockpit] Usando banners do cache válido');
    return globalCache.data;
  }

  // Aguarda headers serem configurados
  console.log('[useStrapiBannersCockpit] Aguardando headers do Strapi...');
  const headersReady = await waitForHeaders();

  if (!headersReady) {
    console.error('[useStrapiBannersCockpit] Headers não configurados após timeout');
    return null;
  }

  console.log('[useStrapiBannersCockpit] Headers configurados, iniciando busca...');

  // Implementa retry com delays progressivos
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      console.log(`[useStrapiBannersCockpit] Tentativa ${attempt + 1}/${MAX_RETRIES + 1}`);

      // Cria timeout para a requisição
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Timeout na requisição')), REQUEST_TIMEOUT);
      });

      // Faz a requisição com timeout
      const response = await Promise.race([
        apiStrapi.get<BannerCockpit>('/organization/banner-cockpit'),
        timeoutPromise,
      ]);

      // Valida a resposta
      if (!response.data || !response.data.cards || !Array.isArray(response.data.cards)) {
        throw new Error('Resposta inválida da API: estrutura de dados incorreta');
      }

      // Atualiza o cache global
      const now = Date.now();
      globalCache = {
        data: response.data,
        timestamp: now,
        expiresAt: now + CACHE_DURATION,
        retryCount: 0,
      };

      console.log('[useStrapiBannersCockpit] Banners carregados com sucesso:', {
        count: response.data.cards.length,
        cacheExpiresAt: new Date(globalCache.expiresAt).toISOString(),
        attempt: attempt + 1,
      });

      return response.data;
    } catch (err) {
      console.error(`[useStrapiBannersCockpit] Erro na tentativa ${attempt + 1}:`, err);

      // Se é a última tentativa, falha
      if (attempt === MAX_RETRIES) {
        console.error('[useStrapiBannersCockpit] Todas as tentativas falharam');
        return null;
      }

      // Se é erro de timeout ou 5xx, tenta novamente
      const shouldRetry =
        err instanceof AxiosError &&
        (err.code === 'ECONNABORTED' ||
          err.message.includes('Timeout') ||
          (err.response?.status && err.response.status >= 500));

      if (shouldRetry) {
        const delay = RETRY_DELAYS[attempt] || RETRY_DELAYS[RETRY_DELAYS.length - 1];
        console.log(
          `[useStrapiBannersCockpit] Aguardando ${delay}ms antes da próxima tentativa...`
        );
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        // Para outros tipos de erro, não tenta novamente
        console.error('[useStrapiBannersCockpit] Erro não recuperável, parando retry');
        return null;
      }
    }
  }

  return null;
};

// Função para inicialização global dos banners
const initializeBannersGlobal = async (): Promise<BannerCockpit | null> => {
  // Se já está inicializando, aguarda
  if (globalIsInitializing && globalInitializationPromise) {
    console.log('[useStrapiBannersCockpit] Aguardando inicialização em andamento...');
    return globalInitializationPromise;
  }

  // Se já temos cache válido, retorna imediatamente
  if (globalCache && Date.now() <= globalCache.expiresAt) {
    console.log('[useStrapiBannersCockpit] Cache válido encontrado, retornando imediatamente');
    return globalCache.data;
  }

  // Inicia nova inicialização
  globalIsInitializing = true;
  globalInitializationPromise = fetchBannersWithRetry().finally(() => {
    globalIsInitializing = false;
    globalInitializationPromise = null;
  });

  return globalInitializationPromise;
};

export const useStrapiBannersCockpit = (): UseStrapiBannersCockpitReturn => {
  const [banners, setBanners] = useState<BannerCockpit | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const isMounted = useRef(true);
  const initializationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Função para inicializar banners
  const initializeBanners = useCallback(async () => {
    if (!isMounted.current) return;

    try {
      setIsLoading(true);
      setError(null);

      console.log('[useStrapiBannersCockpit] Iniciando busca de banners...');
      const result = await initializeBannersGlobal();

      if (!isMounted.current) return;

      if (result) {
        setBanners(result);
        setRetryCount(0);
        console.log('[useStrapiBannersCockpit] Banners inicializados com sucesso');
      } else {
        setError('Não foi possível carregar os banners após múltiplas tentativas');
        console.error('[useStrapiBannersCockpit] Falha na inicialização dos banners');
      }
    } catch (err) {
      if (!isMounted.current) return;

      console.error('[useStrapiBannersCockpit] Erro na inicialização:', err);
      setError('Erro inesperado ao carregar banners');
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // Função para forçar refresh (usada internamente para retry)
  const refreshBanners = useCallback(async () => {
    if (!isMounted.current) return;

    console.log('[useStrapiBannersCockpit] Forçando refresh dos banners...');

    // Limpa cache global para forçar nova busca
    globalCache = null;

    // Incrementa contador de retry
    setRetryCount(prev => prev + 1);

    // Reinicia busca
    await initializeBanners();
  }, [initializeBanners]);

  // Efeito principal - inicializa banners quando o componente monta
  useEffect(() => {
    isMounted.current = true;

    // Inicia busca imediatamente
    initializeBanners();

    // Cleanup
    return () => {
      isMounted.current = false;
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }
    };
  }, [initializeBanners]);

  // Efeito para aguardar inicialização da app
  useEffect(() => {
    const handleUserContextInitialized = () => {
      console.log('[useStrapiBannersCockpit] UserContext inicializado, verificando banners...');

      // Aguarda um pouco para garantir que tudo está pronto
      setTimeout(() => {
        if (isMounted.current && !banners && !isLoading) {
          console.log('[useStrapiBannersCockpit] Reiniciando busca após UserContext...');
          initializeBanners();
        }
      }, 500);
    };

    const handleForceMenuSelection = () => {
      console.log('[useStrapiBannersCockpit] Menu selecionado, verificando banners...');

      // Aguarda um pouco para garantir que tudo está pronto
      setTimeout(() => {
        if (isMounted.current && !banners && !isLoading) {
          console.log('[useStrapiBannersCockpit] Reiniciando busca após seleção de menu...');
          initializeBanners();
        }
      }, 300);
    };

    // Adiciona listeners para eventos de inicialização
    window.addEventListener('userContextInitialized', handleUserContextInitialized);
    window.addEventListener('forceMenuSelection', handleForceMenuSelection);

    return () => {
      window.removeEventListener('userContextInitialized', handleUserContextInitialized);
      window.removeEventListener('forceMenuSelection', handleForceMenuSelection);
    };
  }, [banners, isLoading, initializeBanners]);

  // Efeito para retry automático em caso de falha
  useEffect(() => {
    if (error && retryCount < 3 && isMounted.current) {
      console.log(
        `[useStrapiBannersCockpit] Tentativa de retry automático ${retryCount + 1}/3 em 5s...`
      );

      initializationTimeoutRef.current = setTimeout(() => {
        if (isMounted.current) {
          refreshBanners();
        }
      }, 5000);
    }
  }, [error, retryCount, refreshBanners]);

  return {
    banners,
    isLoading,
    error,
    refreshBanners,
  };
};
